import { canRenderAdvanced } from "../audio/models/modelRouter";
import { getValidationTarget } from "../audio/models/validationTargets";
import { getDefaultVariantId } from "../data/instruments";
import { noteNameToFrequency } from "../lib/notes";
import { AUDIO_LAB_ENGINE_VERSION } from "./manifest";
import { getAudioLabInstrumentById } from "./audioLabRegistry";

export interface AudioFeatureReport {
  engineVersion: string;
  advancedSynthesisEnabled: boolean;
  instrumentId: string;
  instrumentName: string;
  variantId: string;
  note: string;
  articulation: string;
  controlsUsed: Record<string, number | boolean | string>;
  durationSec: number;
  sampleRate: number;
  estimatedFundamentalHz: number;
  rms: number;
  peak: number;
  attackTimeMs: number;
  decayTimeMs: number;
  spectralCentroidHz: number;
  zeroCrossingRate: number;
  highBandEnergyRatio: number;
  lowBandEnergyRatio: number;
  spectralFlatness: number;
  spectralRolloff85: number;
  inharmonicityEstimate: number;
  partialEnergyRatios: number[];
  attackNoiseRatio: number;
  decaySlope: number;
  pitchDriftCents: number;
  vibratoRateEstimate: number;
  vibratoDepthCentsEstimate: number;
  roughnessOrBeatEstimate: number;
  formantPeakCandidates: number[];
  transientDensity: number;
  validationHints: string[];
  knownApproximationWarning: string;
  featureSummary: Record<string, number | number[]>;
  harmonicToNoiseHint: "approximate";
  approximate: true;
  renderedAt: string;
}

export interface ExtractAudioFeaturesOptions {
  audioBuffer: AudioBuffer;
  instrumentId: string;
  note: string;
  articulation: string;
  durationSec: number;
  sampleRate: number;
  variantId?: string;
  controlsUsed?: Record<string, number | boolean | string>;
}

interface Spectrum {
  magnitudes: number[];
  frequencies: number[];
  totalEnergy: number;
}

function roundMetric(value: number, decimals = 4): number {
  if (!Number.isFinite(value)) {
    return 0;
  }

  const multiplier = 10 ** decimals;

  return Math.round(value * multiplier) / multiplier;
}

function getMonoChannel(audioBuffer: AudioBuffer): Float32Array {
  if (audioBuffer.numberOfChannels === 1) {
    return audioBuffer.getChannelData(0);
  }

  const length = audioBuffer.length;
  const mono = new Float32Array(length);

  for (let channel = 0; channel < audioBuffer.numberOfChannels; channel += 1) {
    const data = audioBuffer.getChannelData(channel);

    for (let index = 0; index < length; index += 1) {
      mono[index] += data[index] / audioBuffer.numberOfChannels;
    }
  }

  return mono;
}

function calculateEnvelope(data: Float32Array, windowSize: number): Float32Array {
  const windowCount = Math.max(1, Math.ceil(data.length / windowSize));
  const envelope = new Float32Array(windowCount);

  for (let windowIndex = 0; windowIndex < windowCount; windowIndex += 1) {
    const start = windowIndex * windowSize;
    const end = Math.min(data.length, start + windowSize);
    let sumSquares = 0;

    for (let index = start; index < end; index += 1) {
      sumSquares += data[index] * data[index];
    }

    envelope[windowIndex] = Math.sqrt(sumSquares / Math.max(1, end - start));
  }

  return envelope;
}

function calculateAttackAndDecay(data: Float32Array, sampleRate: number): { attackTimeMs: number; decayTimeMs: number } {
  const windowSize = Math.max(128, Math.round(sampleRate * 0.01));
  const envelope = calculateEnvelope(data, windowSize);
  let peak = 0;
  let peakIndex = 0;

  for (let index = 0; index < envelope.length; index += 1) {
    if (envelope[index] > peak) {
      peak = envelope[index];
      peakIndex = index;
    }
  }

  if (peak <= 0) {
    return { attackTimeMs: 0, decayTimeMs: 0 };
  }

  const attackThreshold = peak * 0.1;
  const decayThreshold = peak * 0.37;
  const attackIndex = envelope.findIndex((value) => value >= attackThreshold);
  let decayIndex = envelope.length - 1;

  for (let index = peakIndex; index < envelope.length; index += 1) {
    if (envelope[index] <= decayThreshold) {
      decayIndex = index;
      break;
    }
  }

  return {
    attackTimeMs: roundMetric(((attackIndex < 0 ? 0 : attackIndex) * windowSize * 1000) / sampleRate, 2),
    decayTimeMs: roundMetric((Math.max(0, decayIndex - peakIndex) * windowSize * 1000) / sampleRate, 2)
  };
}

function pickAnalysisSegment(data: Float32Array, fraction = 0.18): Float32Array {
  const fftSize = Math.min(4096, 2 ** Math.floor(Math.log2(Math.max(32, data.length))));
  const start = Math.max(0, Math.floor((data.length - fftSize) * fraction));

  return data.slice(start, start + fftSize);
}

function calculateSpectrum(data: Float32Array, sampleRate: number): Spectrum {
  const segment = pickAnalysisSegment(data);
  const fftSize = segment.length;
  const magnitudes: number[] = [];
  const frequencies: number[] = [];
  let totalEnergy = 0;

  for (let bin = 1; bin < fftSize / 2; bin += 1) {
    let real = 0;
    let imaginary = 0;
    const angleStep = (-2 * Math.PI * bin) / fftSize;

    for (let index = 0; index < fftSize; index += 1) {
      const window = 0.5 * (1 - Math.cos((2 * Math.PI * index) / (fftSize - 1)));
      const sample = segment[index] * window;
      const angle = angleStep * index;

      real += sample * Math.cos(angle);
      imaginary += sample * Math.sin(angle);
    }

    const magnitude = Math.sqrt(real * real + imaginary * imaginary);
    const frequency = (bin * sampleRate) / fftSize;

    magnitudes.push(magnitude);
    frequencies.push(frequency);
    totalEnergy += magnitude * magnitude;
  }

  return { magnitudes, frequencies, totalEnergy };
}

function calculateSpectralCentroid(spectrum: Spectrum): number {
  let weightedFrequency = 0;
  let magnitudeSum = 0;

  for (let index = 0; index < spectrum.magnitudes.length; index += 1) {
    weightedFrequency += spectrum.frequencies[index] * spectrum.magnitudes[index];
    magnitudeSum += spectrum.magnitudes[index];
  }

  return magnitudeSum > 0 ? roundMetric(weightedFrequency / magnitudeSum, 2) : 0;
}

function calculateZeroCrossingRate(data: Float32Array): number {
  if (data.length < 2) {
    return 0;
  }

  let crossings = 0;
  let previous = data[0];

  for (let index = 1; index < data.length; index += 1) {
    const current = data[index];

    if ((previous >= 0 && current < 0) || (previous < 0 && current >= 0)) {
      crossings += 1;
    }

    previous = current;
  }

  return roundMetric(crossings / (data.length - 1), 5);
}

function energyRatioInBand(spectrum: Spectrum, minHz: number, maxHz: number): number {
  if (spectrum.totalEnergy <= 0) {
    return 0;
  }

  let energy = 0;

  for (let index = 0; index < spectrum.magnitudes.length; index += 1) {
    const frequency = spectrum.frequencies[index];

    if (frequency >= minHz && frequency <= maxHz) {
      energy += spectrum.magnitudes[index] * spectrum.magnitudes[index];
    }
  }

  return roundMetric(energy / spectrum.totalEnergy, 5);
}

function calculateSpectralFlatness(spectrum: Spectrum): number {
  const epsilon = 1e-12;
  const magnitudes = spectrum.magnitudes.filter((magnitude, index) => magnitude > 0 && spectrum.frequencies[index] < 12000);

  if (magnitudes.length === 0) {
    return 0;
  }

  const geometric = Math.exp(magnitudes.reduce((sum, magnitude) => sum + Math.log(magnitude + epsilon), 0) / magnitudes.length);
  const arithmetic = magnitudes.reduce((sum, magnitude) => sum + magnitude, 0) / magnitudes.length;

  return roundMetric(geometric / Math.max(epsilon, arithmetic), 5);
}

function calculateRolloff(spectrum: Spectrum, rolloff = 0.85): number {
  const target = spectrum.totalEnergy * rolloff;
  let running = 0;

  for (let index = 0; index < spectrum.magnitudes.length; index += 1) {
    running += spectrum.magnitudes[index] * spectrum.magnitudes[index];

    if (running >= target) {
      return roundMetric(spectrum.frequencies[index], 2);
    }
  }

  return 0;
}

function estimatePartialEnergyRatios(spectrum: Spectrum, fundamentalHz: number): number[] {
  const ratios: number[] = [];

  for (let partial = 1; partial <= 6; partial += 1) {
    const center = fundamentalHz * partial;
    const ratio = energyRatioInBand(spectrum, center * 0.965, center * 1.035);

    ratios.push(roundMetric(ratio, 5));
  }

  return ratios;
}

function estimateInharmonicity(spectrum: Spectrum, fundamentalHz: number): number {
  const peaks = findPeakCandidates(spectrum, 8, 120, 8000);

  if (peaks.length === 0 || fundamentalHz <= 0) {
    return 0;
  }

  const deviations = peaks.map((peak) => {
    const nearestPartial = Math.max(1, Math.round(peak / fundamentalHz));
    const harmonicHz = fundamentalHz * nearestPartial;

    return Math.abs(peak - harmonicHz) / Math.max(1, harmonicHz);
  });

  return roundMetric(deviations.reduce((sum, value) => sum + value, 0) / deviations.length, 5);
}

function findPeakCandidates(spectrum: Spectrum, count: number, minHz: number, maxHz: number): number[] {
  const peaks: Array<{ frequency: number; magnitude: number }> = [];

  for (let index = 1; index < spectrum.magnitudes.length - 1; index += 1) {
    const frequency = spectrum.frequencies[index];
    const magnitude = spectrum.magnitudes[index];

    if (frequency < minHz || frequency > maxHz) {
      continue;
    }

    if (magnitude > spectrum.magnitudes[index - 1] && magnitude > spectrum.magnitudes[index + 1]) {
      peaks.push({ frequency, magnitude });
    }
  }

  return peaks
    .sort((a, b) => b.magnitude - a.magnitude)
    .slice(0, count)
    .map((peak) => roundMetric(peak.frequency, 2))
    .sort((a, b) => a - b);
}

function estimatePitchWindows(data: Float32Array, sampleRate: number): number[] {
  const windowSize = Math.max(512, Math.round(sampleRate * 0.05));
  const frequencies: number[] = [];

  for (let start = 0; start + windowSize < data.length; start += windowSize) {
    const window = data.slice(start, start + windowSize);
    let crossings = 0;
    let previous = window[0];

    for (let index = 1; index < window.length; index += 1) {
      const current = window[index];

      if ((previous >= 0 && current < 0) || (previous < 0 && current >= 0)) {
        crossings += 1;
      }

      previous = current;
    }

    const estimatedHz = (crossings * sampleRate) / (2 * window.length);

    if (estimatedHz > 20 && estimatedHz < 2400) {
      frequencies.push(estimatedHz);
    }
  }

  return frequencies;
}

function estimatePitchDrift(data: Float32Array, sampleRate: number, expectedHz: number): { drift: number; vibratoRate: number; vibratoDepth: number } {
  const frequencies = estimatePitchWindows(data, sampleRate);

  if (frequencies.length < 2 || expectedHz <= 0) {
    return { drift: 0, vibratoRate: 0, vibratoDepth: 0 };
  }

  const cents = frequencies.map((frequency) => 1200 * Math.log2(frequency / expectedHz));
  const min = Math.min(...cents);
  const max = Math.max(...cents);
  let directionChanges = 0;

  for (let index = 2; index < cents.length; index += 1) {
    const previousSlope = cents[index - 1] - cents[index - 2];
    const slope = cents[index] - cents[index - 1];

    if (previousSlope * slope < 0) {
      directionChanges += 1;
    }
  }

  const duration = data.length / sampleRate;
  const vibratoRate = duration > 0 ? directionChanges / (duration * 2) : 0;

  return {
    drift: roundMetric(max - min, 2),
    vibratoRate: roundMetric(vibratoRate, 2),
    vibratoDepth: roundMetric((max - min) / 2, 2)
  };
}

function calculateDecaySlope(data: Float32Array, sampleRate: number): number {
  const envelope = calculateEnvelope(data, Math.max(128, Math.round(sampleRate * 0.02)));

  if (envelope.length < 4) {
    return 0;
  }

  const peakIndex = envelope.reduce((best, value, index) => (value > envelope[best] ? index : best), 0);
  const tail = Array.from(envelope.slice(peakIndex + 1)).filter((value) => value > 0.00001);

  if (tail.length < 2) {
    return 0;
  }

  const first = Math.log(tail[0]);
  const last = Math.log(tail[tail.length - 1]);
  const seconds = (tail.length * Math.max(128, Math.round(sampleRate * 0.02))) / sampleRate;

  return roundMetric((last - first) / Math.max(0.001, seconds), 5);
}

function estimateRoughnessOrBeat(data: Float32Array, sampleRate: number): number {
  const windowSize = Math.max(256, Math.round(sampleRate * 0.02));
  const envelope = calculateEnvelope(data, windowSize);

  if (envelope.length < 4) {
    return 0;
  }

  let mean = 0;

  for (const value of envelope) {
    mean += value;
  }

  mean /= envelope.length;

  let variance = 0;

  for (const value of envelope) {
    variance += (value - mean) ** 2;
  }

  return roundMetric(Math.sqrt(variance / envelope.length) / Math.max(0.0001, mean), 5);
}

function estimateTransientDensity(data: Float32Array, sampleRate: number): number {
  const windowSize = Math.max(128, Math.round(sampleRate * 0.01));
  const envelope = calculateEnvelope(data, windowSize);

  if (envelope.length < 3) {
    return 0;
  }

  const peak = Math.max(...Array.from(envelope));
  let transients = 0;

  for (let index = 1; index < envelope.length; index += 1) {
    if (envelope[index] - envelope[index - 1] > peak * 0.18) {
      transients += 1;
    }
  }

  return roundMetric(transients / Math.max(0.001, data.length / sampleRate), 4);
}

function calculateAttackNoiseRatio(data: Float32Array, sampleRate: number): number {
  const attackFrames = Math.min(data.length, Math.max(512, Math.round(sampleRate * 0.08)));
  const attackSpectrum = calculateSpectrum(data.slice(0, attackFrames), sampleRate);

  return energyRatioInBand(attackSpectrum, 2000, sampleRate / 2);
}

function inferControlsUsed(articulation: string): Record<string, number | boolean | string> {
  const controls: Record<string, number | boolean | string> = {};

  if (articulation === "nonghyeon" || articulation === "vibrato") {
    controls.nonghyeonDepth = 0.62;
  }

  if (articulation === "meend") {
    controls.microBendAmount = 0.62;
    controls.meendMode = "standard";
  }

  if (articulation === "bayan_pressure" || articulation === "pressure") {
    controls.bayanPressure = 0.72;
  }

  if (articulation === "mouth_shape_xy" || articulation === "vocalization") {
    controls.mouthShapeY = 0.68;
    controls.vocalization = 0.62;
  }

  if (articulation === "ombak") {
    controls.ombakDepth = 0.75;
  }

  if (articulation === "buzz") {
    controls.buzzAmount = 0.8;
  }

  return controls;
}

export function extractAudioFeatures(options: ExtractAudioFeaturesOptions): AudioFeatureReport {
  const { audioBuffer, instrumentId, note, articulation, durationSec, sampleRate } = options;
  const instrument = getAudioLabInstrumentById(instrumentId);
  const variantId = options.variantId ?? getDefaultVariantId(instrumentId);
  const data = getMonoChannel(audioBuffer);
  const spectrum = calculateSpectrum(data, sampleRate);
  const expectedFundamental = noteNameToFrequency(note);
  let sumSquares = 0;
  let peak = 0;

  for (let index = 0; index < data.length; index += 1) {
    const absolute = Math.abs(data[index]);

    sumSquares += data[index] * data[index];
    peak = Math.max(peak, absolute);
  }

  const { attackTimeMs, decayTimeMs } = calculateAttackAndDecay(data, sampleRate);
  const pitch = estimatePitchDrift(data, sampleRate, expectedFundamental);
  const highBandEnergyRatio = energyRatioInBand(spectrum, 2000, sampleRate / 2);
  const lowBandEnergyRatio = energyRatioInBand(spectrum, 0, 250);
  const partialEnergyRatios = estimatePartialEnergyRatios(spectrum, expectedFundamental);
  const validation = getValidationTarget(instrumentId);
  const formantPeakCandidates = findPeakCandidates(spectrum, 5, 250, 5200);
  const featureSummary = {
    highBandEnergyRatio,
    lowBandEnergyRatio,
    spectralFlatness: calculateSpectralFlatness(spectrum),
    inharmonicityEstimate: estimateInharmonicity(spectrum, expectedFundamental),
    pitchDriftCents: pitch.drift,
    roughnessOrBeatEstimate: estimateRoughnessOrBeat(data, sampleRate),
    formantPeakCandidates
  };

  return {
    engineVersion: AUDIO_LAB_ENGINE_VERSION,
    advancedSynthesisEnabled: canRenderAdvanced(instrumentId),
    instrumentId,
    instrumentName: instrument.name,
    variantId,
    note,
    articulation,
    controlsUsed: options.controlsUsed ?? inferControlsUsed(articulation),
    durationSec,
    sampleRate,
    estimatedFundamentalHz: roundMetric(expectedFundamental, 2),
    rms: roundMetric(Math.sqrt(sumSquares / Math.max(1, data.length)), 5),
    peak: roundMetric(peak, 5),
    attackTimeMs,
    decayTimeMs,
    spectralCentroidHz: calculateSpectralCentroid(spectrum),
    zeroCrossingRate: calculateZeroCrossingRate(data),
    highBandEnergyRatio,
    lowBandEnergyRatio,
    spectralFlatness: featureSummary.spectralFlatness as number,
    spectralRolloff85: calculateRolloff(spectrum),
    inharmonicityEstimate: featureSummary.inharmonicityEstimate as number,
    partialEnergyRatios,
    attackNoiseRatio: calculateAttackNoiseRatio(data, sampleRate),
    decaySlope: calculateDecaySlope(data, sampleRate),
    pitchDriftCents: pitch.drift,
    vibratoRateEstimate: pitch.vibratoRate,
    vibratoDepthCentsEstimate: pitch.vibratoDepth,
    roughnessOrBeatEstimate: featureSummary.roughnessOrBeatEstimate as number,
    formantPeakCandidates,
    transientDensity: estimateTransientDensity(data, sampleRate),
    validationHints: validation ? [validation.summary, ...validation.expectedFeatures] : [],
    knownApproximationWarning:
      validation?.approximationWarning ??
      "Browser-side synthetic feature metrics are approximate and require reference review.",
    featureSummary,
    harmonicToNoiseHint: "approximate",
    approximate: true,
    renderedAt: new Date().toISOString()
  };
}
